import { css } from '@emotion/react'
import { Fragment, memo, useCallback, useMemo } from 'react'
import { goToModelsYouMayLike } from '../../../../actions/nav'
import { MYML_LOCATIONS } from '../../../../mfcLogic/api/modelsYouMayLike'
import { isLounge } from '../../../../mfcLogic/userProperties/user'
import i18n from '../../../../utilities/i18n'
import Button from '../../../Buttons/Button'
import { AppStateType, useAppStateSelector } from '../../../Hooks/useAppState'
import { useLayoutSelector } from '../../../Hooks/useLayout'
import { useModelColor } from '../../../Hooks/useModelColor'
import useModelsYouMayLike from '../../../Hooks/useModelsYouMayLike'
import { useUserSelector } from '../../../Hooks/useUser'
import { getThemeSpacing } from '../../../styles/appTheme'
import APP_BREAKPOINTS, {
    maxWindowWidth,
    minWindowWidth,
} from '../../../styles/breakpoints'
import { palette } from '../../../styles/colors'
import { noResultStyles } from '../shared'
import Grid from './Grid'
import List from './List'

const rootStyles = css({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
})

const titleStyles = css({
    textAlign: 'center',
    fontWeight: 500,
    marginBottom: getThemeSpacing(1),
    [maxWindowWidth(APP_BREAKPOINTS.s)]: {
        fontSize: 14,
    },
    [maxWindowWidth(400)]: {
        fontSize: 12,
    },
})

const gridButtonStyles = css({
    paddingTop: 4,
    display: 'block',
    width: '240px',
})

/** Hide elements depending on the screen size */
const gridStyles = css({
    display: 'grid',
    rowGap: 6,
    columnGap: 6,
    gridTemplateColumns: '33% 33% 33%',
    gridTemplateRows: 'auto',
    [maxWindowWidth(767)]: {
        '& div:nth-child(n+4)': {
            display: 'none',
        },
    },
    [minWindowWidth(768)]: {
        gridTemplateRows: 'auto auto',
        columnGap: 8,
        rowGap: 8,
        '& div:nth-child(n+4)': {
            display: 'contents',
        },
    },
})

const moreButtonStyles = css({
    marginTop: getThemeSpacing(0.5),
    fontWeight: 600,
    minWidth: '100%',
    [maxWindowWidth(APP_BREAKPOINTS.sm)]: {
        marginBottom: getThemeSpacing(1),
        fontSize: 12.5,
        lineHeight: 1.96,
    },
})

const morePosterButtonStyles = css({
    background: palette.drawerBackground,
    fontWeight: 600,
    minWidth: '100%',
    [maxWindowWidth(APP_BREAKPOINTS.m)]: {
        fontSize: 12.5,
        lineHeight: 1.96,
        padding: 0,
    },
})

interface RecommendationProps {
    hideNoResultNotice?: boolean
    id: number
    maxResultCount?: number
    inRoomOrProfile?: boolean
    showAsGrid?: boolean
}

const Title = ({ color, name }: { color: string; name: string }) => {
    return (
        <div css={titleStyles}>
            {i18n.t('modelsYouMayLike.ifYouLike')}{' '}
            <span
                style={{
                    color,
                }}
            >
                {name}
            </span>
            {i18n.t('modelsYouMayLike.resultMessageEnd')}
        </div>
    )
}

const Content = ({
    id,
    inRoomOrProfile = false,
    hideNoResultNotice = false,
    maxResultCount = 6,
    showAsGrid = false,
}: RecommendationProps) => {
    const { modelResults } = useModelsYouMayLike(
        id,
        MYML_LOCATIONS.menu,
        inRoomOrProfile,
    )
    const RecommendationComponent = showAsGrid ? Grid : List
    const modelColor = useUserSelector(id, 'color')
    const name = useUserSelector(id, 'name')
    const color = useModelColor(modelColor)
    const inLounge = isLounge(name)

    const topModels = useMemo(
        () => modelResults.slice(0, maxResultCount),
        [modelResults, maxResultCount],
    )

    const onOpenMoreModelsYouMayLike = useCallback(() => {
        goToModelsYouMayLike(name)
    }, [name])

    if (topModels.length === 0) {
        if (hideNoResultNotice) return <Fragment />
        return (
            <article
                css={noResultStyles}
                data-testid="ModelsYouMayLike__NoResults"
            >
                <p>
                    {i18n.t('modelsYouMayLike.noRecommendations')}{' '}
                    <span style={{ color, fontWeight: 'bold' }}>{name}</span>.
                </p>
            </article>
        )
    }

    return (
        <>
            <Title name={name} color={color} />
            <article
                key={id}
                data-testid="ModelsYouMayLike__ResultRows"
                css={showAsGrid ? gridStyles : {}}
            >
                {topModels.map((model) => (
                    <div key={model.user_id}>
                        <RecommendationComponent
                            id={model.user_id}
                            recommendation={model.similarity}
                            inRoomOrProfile={inRoomOrProfile}
                        />
                    </div>
                ))}
            </article>
            {!inLounge && (
                <article css={showAsGrid ? gridButtonStyles : {}}>
                    <Button
                        data-testid="ModelsYouMayLike__Button"
                        onClick={onOpenMoreModelsYouMayLike}
                        css={
                            showAsGrid
                                ? morePosterButtonStyles
                                : moreButtonStyles
                        }
                        size="medium"
                        border
                        clean
                    >
                        {i18n.t('modelsYouMayLike.moreModels')}
                    </Button>
                </article>
            )}
        </>
    )
}

const Recommendation = (props: RecommendationProps) => {
    const { id, showAsGrid } = props
    const fullWindow = useLayoutSelector('fullWindow')
    const isMobile = useAppStateSelector(AppStateType.APP, 'isMobile')
    // hide when in full window mode on mobile due to chat overlap
    const hideDueToSizeConstraints = fullWindow && isMobile && showAsGrid

    if (hideDueToSizeConstraints) return null

    return (
        <div
            css={rootStyles}
            style={showAsGrid ? { alignItems: 'center' } : {}}
            key={id}
            data-testid="ModelsYouMayLike__Container"
        >
            <Content {...props} />
        </div>
    )
}

export default memo(Recommendation)
